import { getResourceInfo } from '../resource/resource.mjs';
import { setAgentAccess as setAgentAccess$1 } from '../acp/util/setAgentAccess.mjs';
import { setAgentResourceAccess, getAgentAccess } from '../access/wac.mjs';
import { getResourceAcr } from '../acp/util/getResourceAcr.mjs';
import { saveAcrFor } from '../acp/acp.mjs';
import { getAgentAccess as getAgentAccess$1 } from './getAgentAccess.mjs';

//
/**
 * Set access to a resource for a given Agent.
 *
 * This function works with Solid Pods that implement either the Web Access
 * Control spec or the Access Control Policies proposal, with some caveats:
 *
 * - If access to the given Resource has been set using anything other than the
 *   functions in this module, it is possible that it has been set in a way that
 *   prevents this function from reliably setting access.
 * - It will only set access explicitly for the given Agent. In other words,
 *   additional restrictions could be present that further restrict or loosen
 *   what access the given Agent has in particular circumstances.
 * - The provided access will only apply to the given Resource. In other words,
 *   if the Resource is a Container, the configured Access will not apply to
 *   contained Resources.
 * - If the current user does not have permission to view or change access for
 *   the given Resource, this function will resolve to `null`.
 *
 * Additionally, two caveats apply to users with a Pod server that uses WAC:
 * - If the Resource did not have an ACL yet, a new one will be initialised.
 *   This means that changes to the ACL of a parent Container can no longer
 *   affect access people have to this Resource, although existing access will
 *   be preserved.
 * - Setting different values for `controlRead` and `controlWrite` is not
 *   supported, and **will throw an error**. If you expect (some of) your users
 *   to have Pods implementing WAC, be sure to pass the same value for both.
 *
 * @param resourceUrl URL of the Resource you want to set access for.
 * @param webId WebID of the Agent you want to set access for.
 * @param access The Access Modes to add (true) or remove (false).
 * @param options Default Options such as a fetch function.
 * @since 1.19.0
 */
async function setAgentAccess(resourceUrl, webId, access, options) {
    // TODO: Change the standard getAgentAccess signatures to all take a  T extends WithAcl
    const resourceInfo = await getResourceInfo(resourceUrl, options);
    const acr = await getResourceAcr(resourceInfo, options);
    if (acr === null) {
        await setAgentResourceAccess(resourceInfo, webId, access, options);
        return getAgentAccess(resourceInfo, webId, options);
    }
    // TODO: Make sure both setAgentAccessWac and setAgentAccessAcp don't save within the function, expose one standard saveAclFor function that is universal.
    try {
        await saveAcrFor(await setAgentAccess$1(acr, webId, access), options);
        return await getAgentAccess$1(resourceUrl, webId, options);
    }
    catch (e) {
        return null;
    }
}

export { setAgentAccess };
